import { memo, useId } from "react";
import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { formatValueWithSign, toFiniteNumber } from "components/ui/formatters";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";
import { SingleValueCardProps } from "helpers/types";
import EditableTextField from "./EditableTextField";
import {
  UNAVAILABLE_DISPLAY,
  UnavailableReasonIcon,
  isPlaceholderDisplay,
  joinFieldStateText,
  notedFieldSx,
  placeholderFieldSx,
  unavailableFieldSx,
  visuallyHiddenSx,
} from "./reportFieldStates";

const SingleValueCard = memo(({
  value,
  label,
  description,
  type,
  color,
  onValueChange,
  unavailableReason,
  note,
  dimmed = false,
  overridable = false,
  onClear,
}: SingleValueCardProps) => {
  const reasonId = useId();

  // The reason ONLY when it actually says something. A reason is the whole
  // invitation the icon extends, so a blank one has to be treated as no
  // reason at all: an icon promising an explanation and opening an empty
  // tooltip is worse than the unmarked N/A below it. Deliberately narrower
  // than it looks -- the DISPLAY still keys off the raw prop, so a blank
  // reason reads N/A exactly as it did before and does not fall through to
  // a $0.00 that would claim a figure the backend refused.
  const explanation = unavailableReason?.trim() ? unavailableReason : undefined;

  // The card's own reading of what it was handed, done once. `value` is a
  // `number | string` off a localStorage-backed hook, so "a figure" is a
  // question to be answered rather than a fact the type guarantees -- null
  // here means there is nothing to format, colour, or seed an edit from.
  const numericValue = toFiniteNumber(value);

  // A percentage that is not a usable number has no figure to show. It reads
  // N/A rather than crashing on `value.toFixed`, and rather than showing 0 --
  // a time-weighted return of 0.00% claims the strategy went exactly nowhere,
  // which is a result, and the backend's point was that it produced none.
  // Scoped to PERCENTAGE on purpose: the CURRENCY and NUMBER cards render a
  // wrong-but-harmless $0.00 today and changing ten shipped cards' display is
  // a separate decision, named as a follow-up.
  const isMissingPercentage =
    type === SingleValueCardType.PERCENTAGE && numericValue === null;

  // A NULLISH value has no figure on ANY card type, and that is the one place
  // this card does not defer to the scoping above. The distinction is what the
  // two absences MEAN. "" is the report layer's own sentinel, written
  // deliberately, and for a currency row the $0.00 it renders is a shipped
  // display this change is not entitled to alter. A null is not a sentinel and
  // was never written on purpose: it is an unread payload arriving where the
  // types promised a number, so there is no shipped behaviour to preserve --
  // only a $0.00 that would state, in a client-facing figure, a starting value
  // the backend never produced. Five of the six summary cards are CURRENCY, so
  // scoping this to PERCENTAGE would leave the reported crash's own cards
  // asserting zero.
  const isMissingValue = value === null || value === undefined;

  // "No Report" / "NOT IMPLEMENTED" -- the seed this card was handed because
  // no backend figure has replaced it yet. It is shown VERBATIM, which is the
  // whole point: the formatters would render it "$0.00" for a currency card
  // and "" for a percentage one, and $0.00 is precisely the invented figure
  // this state exists to stop. reportFieldStates says which of the two seeds
  // means what.
  const placeholderDisplay = isPlaceholderDisplay(value) ? String(value) : null;

  const getDisplayColor = () => {
    // A colour asserts a direction, and a figure that was never produced has
    // none -- green would read as a gain and red as a loss on nothing. The
    // seed states are the same case: the card's own `color` prop is a fixed
    // per-card choice made for a figure, so it has to be dropped here rather
    // than allowed to fall through and paint a placeholder.
    if (unavailableReason || placeholderDisplay) {
      return SingleValueCardColor.PRIMARY;
    }
    if (type === SingleValueCardType.PERCENTAGE && numericValue !== null) {
      if (numericValue > 0) return SingleValueCardColor.SUCCESS;
      if (numericValue < 0) return SingleValueCardColor.ERROR;
    }
    return color || SingleValueCardColor.PRIMARY;
  };

  const displayColor = getDisplayColor();
  // Precedence, and each step of it is a decision rather than an ordering
  // accident. A REASON wins outright: it means the backend ran, reached this
  // figure and refused it, which is an answer, where a seed only says nothing
  // has run. The seed comes next, ahead of the missing-percentage N/A below,
  // because a seeded percentage is not a figure the backend withheld -- it is
  // one nobody has asked for yet, and N/A would say the wrong thing about it.
  // A nullish value joins the missing-percentage N/A on the same rung rather
  // than above the seed: the two cannot both be true (a seed is one of three
  // strings, and null is none of them), so their order says nothing, and
  // putting them together is what keeps this a two-decision precedence
  // instead of a three-way one.
  const formattedValue = unavailableReason
    ? UNAVAILABLE_DISPLAY
    : (placeholderDisplay ??
      (isMissingValue || isMissingPercentage
        ? UNAVAILABLE_DISPLAY
        : formatValueWithSign(value, type)));

  // N/A has to read as a STATE and not as a value, so the muting keys off the
  // display rather than off the reason: an N/A with no surviving reason is
  // still an N/A, and left in full figure weight and navy it looked exactly
  // like a real figure. reportFieldStates spells out why that matters. The
  // tooltip and the hidden reason node stay keyed off the reason itself --
  // there is nothing to explain when none came with it.
  const isUnavailableDisplay = formattedValue === UNAVAILABLE_DISPLAY;

  // Every non-figure gets the muting, not just N/A: "NOT IMPLEMENTED" in full
  // figure weight and navy reads as a value, which is the same failure in a
  // different font.
  const isStateDisplay = isUnavailableDisplay || placeholderDisplay !== null;

  // The backend's advisory about a figure it DID produce -- the mirror image
  // of the reason above rather than another form of it: a Bucket 3 card
  // measured from the bucket's creation date is a real number over a window
  // the reader did not ask for. Trimmed on the same terms as the reason, and
  // for the same reason: a blank one would put an icon on the card that opens
  // an empty tooltip.
  //
  // It is dropped where the card is showing a STATE rather than a figure and
  // no reason came with it -- a seed, or the missing-percentage N/A. There is
  // no figure there for a note to qualify, and hanging one off "No Report"
  // would promise an explanation of a number nobody produced. A reason is the
  // exception, and is handled by `explanation` above: it IS an answer about
  // this run, and when the two arrive together the tooltip carries both.
  const trimmedNote = note?.trim() ? note : undefined;
  const advisory = isStateDisplay && !explanation ? undefined : trimmedNote;
  // One text for one tooltip, reason first when both arrive; the join and the
  // order are in reportFieldStates, where MultiValuesCard reads them too.
  const stateText = joinFieldStateText(explanation, advisory);

  // The field's wrapper for each state -- see the render below for why it is
  // one wrapper whose styling varies rather than a different tree per state.
  const wrapperSx = stateText
    ? explanation
      ? unavailableFieldSx
      : notedFieldSx
    : placeholderDisplay
      ? placeholderFieldSx
      : isStateDisplay
        ? unavailableFieldSx
        : undefined;

  const valueField = (
    <EditableTextField
      key="field"
      // A dimmed tile withdraws its override too (operator, task 3580). The
      // two are one state rather than two choices that happen to coincide:
      // the tile is dimmed because the figure does not answer the question
      // the scope switch just asked, and an override typed into a figure
      // nobody is meant to be reading is the one edit on this card that can
      // be made by accident. Read-only rather than hidden or blanked --
      // the figure is real and still correct for the period, so it stays
      // legible and stays in the tab order. An `overridable` tile is the one
      // exception (operator, task 3781): the reader may state their own
      // figure over one the page withheld, and doing so undims it.
      readOnly={dimmed && !overridable}
      // The resolved number, never the raw union -- what MultiValuesCard hands
      // down too. The editor seeds itself from `value.toString()`, which is
      // the second throw waiting in this chain for a genuine null. It never
      // reads this 0 for a non-figure, because `hasFigure` below tells it not
      // to, so focusing an empty card starts empty.
      value={numericValue ?? 0}
      formattedValue={formattedValue}
      hasFigure={!isStateDisplay && numericValue !== null}
      type={type}
      displayColor={displayColor}
      onValueChange={onValueChange}
      onClear={onClear}
      // Whatever the tooltip says, a screen reader parked on the input has to
      // hear: a note is as invisible to it as a reason was.
      describedById={stateText ? reasonId : undefined}
    />
  );

  return (
    <Card
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        // The WHOLE tile recedes, label and footer with it (operator, task
        // 3580) -- muting the figure alone is already this card's vocabulary
        // for "there is no figure here" (reportFieldStates), and these tiles
        // have a perfectly good one. What is being said is about the TILE:
        // it is not part of the answer on screen. So it is one opacity on
        // the card rather than three treatments inside it, which also keeps
        // the dimming off the figure's own colour -- a red Margin Interest
        // stays red, just quieter, instead of being recoloured into the
        // grey this card reserves for N/A.
        //
        // 0.55 rather than the 0.45 used for an excluded bucket member: this
        // tile is still a correct figure a reader may want, and at 0.45 the
        // navy figures on white went from de-emphasised to hard to read.
        // De-emphasis is the ask; illegibility is a different outcome.
        opacity: dimmed ? 0.55 : 1,
        transition: "opacity 150ms ease-in-out",
      }}
    >
      <CardContent>
        <Typography variant="h5">{label}</Typography>

        {/* One wrapper, one hidden copy of the text, one tooltip --
            reportFieldStates explains why each of those is needed, and that
            the three are the same whether the text is a reason or a note.

            Only the wrapper differs between the two. A reason is displaying
            "N/A" and takes the muting that makes those two characters read as
            a state; a note sits beside a figure that was produced, which
            keeps its full weight and its own colour, because a muted figure
            reads as unavailable and the note's whole claim is that it is not.
            A reason present alongside a note still wins the display and the
            muting -- the field really is showing N/A. */}
        {/* ONE tree for every state, so the field is never remounted when
            the state changes under it (operator, task 3781): a reader who
            types a figure over Net Value's withheld N/A turns the card from
            reason to figure on that very keystroke, and a remount there drops
            their focus to <body> -- the next Tab then lands on the page's
            first tab stop. Only the wrapper's sx and the two reason-only
            children vary; an empty tooltip title never opens. */}
        <Tooltip title={stateText ?? ""} describeChild enterTouchDelay={0}>
          <Box sx={wrapperSx}>
            {stateText && <UnavailableReasonIcon key="icon" />}
            {valueField}
            {stateText && (
              <Box
                key="reason"
                component="span"
                id={reasonId}
                sx={visuallyHiddenSx}
              >
                {stateText}
              </Box>
            )}
          </Box>
        </Tooltip>

        <Typography variant="body1">{description}</Typography>
      </CardContent>
    </Card>
  );
});

export default SingleValueCard;
